import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { SharedComponentsModule } from "@/components/shared/shared-components.module";
import { StatsService } from "@/_services";
import { TerminologyService } from "@/_services/terminology/terminology.service";
import { TermKey } from "@/_services/terminology/catalog";
import { ProgramHealthExpandedStorage } from "@/_services/storage";
import {
    DashboardModule,
    DashboardModuleCounts,
} from "@/_models/dashboard-stats.interface";
import { DASHBOARD_MODULE_ORDER } from "./dashboard-modules.config";

/** Which of the three counts is filtering the work list, if any. */
export type ProgramHealthFilter = "overdue" | "open" | "monitoring" | null;

interface HealthRow {
    key: string;
    label: string;
    /**
     * Whether the active filter has dimmed this row. Held as state rather than
     * computed in the template, which would re-run on every change detection.
     */
    dimmed: boolean;
    /** Null when the account's plan does not include the module. */
    counts: DashboardModuleCounts | null;
    /** Plan that would unlock it, shown when counts are null. */
    unlockedBy: string;
}

@UntilDestroy()
@Component({
    selector: "app-program-health",
    standalone: true,
    imports: [CommonModule, SharedComponentsModule],
    templateUrl: "./program-health.component.html",
    styleUrl: "./program-health.component.scss",
})
export class ProgramHealthComponent implements OnInit {
    /** Emitted when a count is clicked, so the work list can filter to match. */
    @Output() filterChange = new EventEmitter<ProgramHealthFilter>();

    title = "";
    legendOpen = "";
    legendOverdue = "";
    legendMonitoring = "";
    showBreakdownLabel = "";
    upgradeLabel = "";
    hideBreakdownLabel = "";
    summary: DashboardModuleCounts = { open: 0, overdue: 0, monitoring: 0 };
    rows: HealthRow[] = [];
    lockedCount = 0;
    activeFilter: ProgramHealthFilter = null;
    expanded = true;
    loading = true;

    constructor(
        private statsService: StatsService,
        private terminology: TerminologyService,
    ) {}

    ngOnInit(): void {
        this.title = this.term("dashboard.health.title");
        this.legendOpen = this.term("dashboard.health.legend.open");
        this.legendOverdue = this.term("dashboard.health.legend.overdue");
        this.legendMonitoring = this.term("dashboard.health.legend.monitoring");
        this.upgradeLabel = this.term("dashboard.health.upgradeRequired");
        this.showBreakdownLabel = this.term("dashboard.health.showBreakdown");
        this.hideBreakdownLabel = this.term("dashboard.health.hideBreakdown");
        this.expanded = ProgramHealthExpandedStorage.get() ?? true;

        this.statsService
            .getDashboardStats()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (stats) => {
                    this.summary = stats.summary;
                    this.rows = this.buildRows(stats.modules);
                    this.lockedCount = this.rows.filter(
                        (row) => !row.counts,
                    ).length;
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                },
            });
    }

    /**
     * Every module in display order. The server sends figures only for the
     * ones the plan includes, so anything missing from its response becomes a
     * locked row naming the plan that would unlock it.
     */
    private buildRows(modules: DashboardModule[]): HealthRow[] {
        const byKey = new Map(modules.map((module) => [module.key, module]));

        return DASHBOARD_MODULE_ORDER.map((config) => {
            const live = byKey.get(config.key);
            return {
                key: config.key,
                label: this.term(`dashboard.module.${config.key}`),
                dimmed: false,
                counts: live
                    ? {
                          open: live.open,
                          overdue: live.overdue,
                          monitoring: live.monitoring,
                      }
                    : null,
                unlockedBy: config.unlockedBy,
            };
        });
    }

    private term(key: string): string {
        return this.terminology.resolve(key as TermKey);
    }

    /**
     * Clicking a count filters the work list to it. Clicking the active one
     * again clears the filter, so the control is its own undo.
     */
    toggleFilter(filter: Exclude<ProgramHealthFilter, null>): void {
        this.activeFilter = this.activeFilter === filter ? null : filter;
        this.applyDimming();
        this.filterChange.emit(this.activeFilter);
    }

    /**
     * A row is dimmed when it holds none of whatever is being filtered on, so
     * the breakdown visibly agrees with the count above it.
     */
    private applyDimming(): void {
        const active = this.activeFilter;
        this.rows.forEach((row) => {
            row.dimmed = !!active && !!row.counts && row.counts[active] === 0;
        });
    }

    toggleExpanded(): void {
        this.expanded = !this.expanded;
        ProgramHealthExpandedStorage.put(this.expanded);
    }
}
